/** @type {import('tailwindcss').Config} */
export default {
    content: ['./src/**/*.{js,ts,jsx,tsx}'],
    theme: {
        extend: {
            spacing: {
                drawerWidth: '288px',
                tableActionsDropdownWidth: '120px',
                paginationDropdownWidth: '280px',
                filterContentMinWidth: '290px',
                filterContentMinHeight: '380px',
                formPageFormWidth: '360px',
                logoHeight: '32px',
                userDropdownWidth: '160px',
                widgetHeight: '320px',
                tooltipWidth: '242px',
                agentDropdownHeight: '506px',
                chartSurfaceMinHeight: '496px',
                chartTooltipMinWidth: '250px',
                chartTooltipMaxWidth: '500px',
            },
            screens: {
                lg: '1200px',
            },
            animation: {
                'spin-reverse': 'spin 1s linear reverse infinite',
            },
            minWidth: {
                minWidgetWidth: '520px',
            },
        },
        fontFamily: {
            inter: ['Inter Variable', 'sans-serif'],
            raleway: ['Raleway Variable', 'sans-serif'],
        },
        fontSize: {
            '5xl': ['104px', 136 / 104],
            '4xl': ['64px', 80 / 64],
            '3xl': ['48px', 56 / 48],
            '2xl': ['32px', 48 / 32],
            xl: ['24px', 32 / 24],
            lg: ['20px', 32 / 20],
            md: ['18px', 24 / 18],
            base: ['16px', 24 / 16],
            sm: ['14px', 24 / 14],
            'xs-loose': ['12px', 24 / 12],
            xs: ['12px', 18 / 12],
            'xs-tight': ['12px', 16 / 12],
            xxs: ['10px', 16 / 10],

            'icon-xxsmall': ['10px', 1],
            'icon-xsmall': ['14px', 1],
            'icon-small': ['16px', 1],
            'icon-smedium': ['20px', 1],
            'icon-medium': ['24px', 1],
            'icon-large': ['32px', 1],
            'icon-xlarge': ['40px', 1],
        },
        colors: {
            azure: 'rgb(0 129 230 / <alpha-value>)', // #0081E6
            oxfordBlue: 'rgb(23 45 82 / <alpha-value>)', // #172D52
            skyBlue: 'rgb(113 191 255 / <alpha-value>)', // #71BFFF
            white: 'rgb(255 255 255 / <alpha-value>)', // #FFFFFF
            black: 'rgb(6 18 38 / <alpha-value>)', // #061226
            grey: 'rgb(233 234 235 / <alpha-value>)', // #E9EAEB

            // text colors
            regular: 'rgb(38 52 69 / <alpha-value>)', // #263445
            subtle: 'rgb(52 71 95 / <alpha-value>)', // #34475F
            disabled: 'rgb(125 153 188 / <alpha-value>)', // #7D99BC
            placeholder: 'rgb(94 129 172 / <alpha-value>)', // #5E81AC
            linkPrimary: 'rgb(0 129 230 / <alpha-value>)', // #0082e6
            linkSecondary: 'rgb(38 52 69 / <alpha-value>)', // #263445
            success: 'rgb(31 138 43 / <alpha-value>)', // #1F8A2B
            warning: 'rgb(204 113 46 / <alpha-value>)', // #CC712E
            error: 'rgb(253 42 33 / <alpha-value>)', // #FD2A21
            info: 'rgb(38 52 69 / <alpha-value>)', // #263445

            // dark mode: text colors
            regularOnDark: 'rgb(255 255 255 / <alpha-value>)', // #FFFFFF
            subtleOnDark: 'rgb(216 225 235 / <alpha-value>)', // #D8E1EB
            disabledOnDark: 'rgb(80 110 146 / <alpha-value>)', // #506E92
            placeholderOnDark: 'rgb(94 129 172 / <alpha-value>)', // #5E81AC
            linkPrimaryOnDark: 'rgb(153 205 245 / <alpha-value>)', // #99CDF5
            linkSecondaryOnDark: 'rgb(255 255 255 / <alpha-value>)', // #FFFFFF
            successOnDark: 'rgb(121 185 128 / <alpha-value>)', // #79B980
            warningOnDark: 'rgb(255 187 137 / <alpha-value>)', // #FFBB89
            errorOnDark: 'rgb(254 127 122 / <alpha-value>)', // #FE7F7A
            infoOnDark: 'rgb(216 225 235 / <alpha-value>)', // #D8E1EB

            neutral: {
                50: 'rgb(247 249 252 / <alpha-value>)', // #F7F9FC
                100: 'rgb(240 244 249 / <alpha-value>)', // #F0F4F9
                200: 'rgb(226 234 243 / <alpha-value>)', // #E2EAF3
                300: 'rgb(197 214 231 / <alpha-value>)', // #C5D6E7
                400: 'rgb(125 153 188 / <alpha-value>)', // #7D99BC
                500: 'rgb(94 129 172 / <alpha-value>)', // #5E81AC
                600: 'rgb(80 110 146 / <alpha-value>)', // #506E92
                700: 'rgb(66 90 120 / <alpha-value>)', // #425A78
                800: 'rgb(52 71 95 / <alpha-value>)', // #34475F
                900: 'rgb(38 52 69 / <alpha-value>)', // #263445
            },
            blue: {
                50: 'rgb(229 242 252 / <alpha-value>)', // #E5F2FC
                100: 'rgb(204 230 250 / <alpha-value>)', // #CCE6FA
                200: 'rgb(153 205 245 / <alpha-value>)', // #99CDF5
                300: 'rgb(102 179 240 / <alpha-value>)', // #66B3F0
                400: 'rgb(51 154 235 / <alpha-value>)', // #339AEB
                500: 'rgb(0 129 230 / <alpha-value>)', // #0081E6
                600: 'rgb(0 103 184 / <alpha-value>)', // #0067B8
                700: 'rgb(0 77 138 / <alpha-value>)', // #004D8A
                800: 'rgb(0 52 92 / <alpha-value>)', // #00345C
                900: 'rgb(0 26 46 / <alpha-value>)', // #001A2E
            },
            purple: {
                50: 'rgb(228 224 245 / <alpha-value>)', // #E4E0F5
                100: 'rgb(201 193 235 / <alpha-value>)', // #C9C1EB
                200: 'rgb(174 163 225 / <alpha-value>)', // #AEA3E1
                300: 'rgb(147 132 215 / <alpha-value>)', // #9384D7
                400: 'rgb(120 101 205 / <alpha-value>)', // #7865CD
                500: 'rgb(93 70 195 / <alpha-value>)', // #5D46C3
                600: 'rgb(76 55 169 / <alpha-value>)', // #4C37A9
                700: 'rgb(62 45 139 / <alpha-value>)', // #3E2D8B
                800: 'rgb(48 35 108 / <alpha-value>)', // #30236C
                900: 'rgb(35 25 77 / <alpha-value>)', // #23194D
            },
            pink: {
                50: 'rgb(253 235 255 / <alpha-value>)', // #FDEBFF
                100: 'rgb(249 194 255 / <alpha-value>)', // #F9C2FF
                200: 'rgb(245 153 255 / <alpha-value>)', // #F599FF
                300: 'rgb(241 112 255 / <alpha-value>)', // #F170FF
                400: 'rgb(237 71 255 / <alpha-value>)', // #ED47FF
                500: 'rgb(233 31 255 / <alpha-value>)', // #E91FFF
                600: 'rgb(202 0 224 / <alpha-value>)', // #CA00E0
                700: 'rgb(165 0 184 / <alpha-value>)', // #A500B8
                800: 'rgb(108 0 122 / <alpha-value>)', // #6C007A
                900: 'rgb(73 0 82 / <alpha-value>)', // #490052
            },
            red: {
                50: 'rgb(255 234 233 / <alpha-value>)', // #FFEAE9
                100: 'rgb(255 212 211 / <alpha-value>)', // #FFD4D3
                200: 'rgb(254 170 166 / <alpha-value>)', // #FEAAA6
                300: 'rgb(254 127 122 / <alpha-value>)', // #FE7F7A
                400: 'rgb(253 85 77 / <alpha-value>)', // #FD554D
                500: 'rgb(253 42 33 / <alpha-value>)', // #FD2A21
                600: 'rgb(202 34 26 / <alpha-value>)', // #CA221A
                700: 'rgb(152 25 20 / <alpha-value>)', // #981914
                800: 'rgb(101 17 13 / <alpha-value>)', // #65110D
                900: 'rgb(51 8 7 / <alpha-value>)', // #330807
            },
            yellow: {
                50: 'rgb(255 252 235 / <alpha-value>)', // #FFFCEB
                100: 'rgb(255 249 216 / <alpha-value>)', // #FFF9D8
                200: 'rgb(255 243 176 / <alpha-value>)', // #FFF3B0
                300: 'rgb(255 236 137 / <alpha-value>)', // #F7E47C
                400: 'rgb(255 230 97 / <alpha-value>)', // #F9E264
                500: 'rgb(255 224 58 / <alpha-value>)', // #F8D935
                600: 'rgb(204 179 46 / <alpha-value>)', // #CCB32E
                700: 'rgb(153 134 35 / <alpha-value>)', // #998623
                800: 'rgb(102 90 23 / <alpha-value>)', // #665A17
                900: 'rgb(51 45 12 / <alpha-value>)', // #332D0C
            },
            orange: {
                50: 'rgb(255 244 235 / <alpha-value>)', // #FFF4EB
                100: 'rgb(255 232 216 / <alpha-value>)', // #FFE8D8
                200: 'rgb(255 209 176 / <alpha-value>)', // #FFD1B0
                300: 'rgb(255 187 137 / <alpha-value>)', // #FFBB89
                400: 'rgb(255 164 97 / <alpha-value>)', // #FFA461
                500: 'rgb(255 123 28 / <alpha-value>)', // #FF7B1C
                600: 'rgb(224 94 0 / <alpha-value>)', // #E05E00
                700: 'rgb(163 68 0 / <alpha-value>)', // #A34400
                800: 'rgb(122 51 0 / <alpha-value>)', // #7A3300
                900: 'rgb(61 26 0 / <alpha-value>)', // #3D1A00
            },
            green: {
                50: 'rgb(233 243 234 / <alpha-value>)', // #E9F3EA
                100: 'rgb(210 232 213 / <alpha-value>)', // #D2E8D5
                200: 'rgb(165 208 170 / <alpha-value>)', // #A5D0AA
                300: 'rgb(121 185 128 / <alpha-value>)', // #79B980
                400: 'rgb(76 161 85 / <alpha-value>)', // #4CA155
                500: 'rgb(31 138 43 / <alpha-value>)', // #1F8A2B
                600: 'rgb(25 110 34 / <alpha-value>)', // #196E22
                700: 'rgb(19 83 26 / <alpha-value>)', // #13531A
                800: 'rgb(12 55 17 / <alpha-value>)', // #0C3711
                900: 'rgb(6 28 9 / <alpha-value>)', // #061C09
            },
        },
        boxShadow: {
            none: 'none',
            xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            sm: '0 1px 1px 0 rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.1)',
            md: '0 2px 2px 0 rgba(0, 0, 0, 0.06), 0 4px 3px 0 rgba(0, 0, 0, 0.07)',
            lg: '0 4px 3px 0 rgba(0, 0, 0, 0.1), 0 10px 8px 0 rgba(0, 0, 0, 0.04)',
            xl: '0 8px 5px 0 rgba(0, 0, 0, 0.08), 0 20px 13px 0 rgba(0, 0, 0, 0.03)',
            'inset-1': 'inset 0 0 0 1px', // Default value
            'inset-azure': 'inset 0 0 0 1px rgb(0 129 230)', // Custom azure inset shadow
        },
        borderRadius: {
            0: '0',
            sm: '2px',
            DEFAULT: '4px',
            lg: '8px',
            xl: '12px',
            xxl: '16px',
            full: '999em',
        },
    },
    plugins: [],
}
